import { AdaptiveTimeout } from '../utils/adaptiveTimeout';

const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`;

// PRD-robustesse-organisateur-reseau-faible.md (item 3)
const UPLOAD_MAX_ATTEMPTS = 3;
// Un upload d'image est plus lourd qu'un simple appel API : on multiplie le
// timeout adaptatif de base (AdaptiveTimeout.getApiTimeout()) pour laisser le
// temps à l'upload de passer même en 2G/3G, sans pour autant pendre indéfiniment.
const UPLOAD_TIMEOUT_MULTIPLIER = 4;
const MAX_IMAGE_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
// En dessous de ce poids, la compression n'apporte pas grand-chose : on l'évite.
const COMPRESSION_MIN_SIZE_BYTES = 300 * 1024;

/**
 * Options for Cloudinary image optimization
 */
interface OptimizeOptions {
  width?: number;      // Max width (maintains aspect ratio)
  height?: number;     // Max height (maintains aspect ratio)
  quality?: 'auto' | 'auto:low' | 'auto:eco' | 'auto:good' | 'auto:best' | number;
  format?: 'auto' | 'webp' | 'avif';
}

/**
 * Optimizes a Cloudinary URL with automatic format (WebP) and quality
 * @param url - The original Cloudinary URL
 * @param options - Optimization options
 * @returns Optimized URL or original if not a Cloudinary URL
 */
export function optimizeCloudinaryUrl(
  url: string | undefined | null,
  options: OptimizeOptions = {}
): string {
  if (!url) return '';

  // Check if it's a Cloudinary URL
  if (!url.includes('res.cloudinary.com')) {
    return url;
  }

  const {
    width,
    height,
    quality = 'auto',
    format = 'auto'
  } = options;

  // Build transformation string
  const transforms: string[] = [];

  // Format auto = WebP when browser supports it
  transforms.push(`f_${format}`);

  // Quality auto = intelligent compression
  transforms.push(`q_${quality}`);

  // Optional resize (maintains aspect ratio with c_limit)
  if (width) transforms.push(`w_${width}`);
  if (height) transforms.push(`h_${height}`);
  if (width || height) transforms.push('c_limit');

  const transformString = transforms.join(',');

  // Insert transformations after /upload/
  // URL format: https://res.cloudinary.com/cloud_name/image/upload/[transforms]/version/public_id
  const uploadIndex = url.indexOf('/upload/');
  if (uploadIndex === -1) return url;

  const beforeUpload = url.substring(0, uploadIndex + 8); // includes '/upload/'
  const afterUpload = url.substring(uploadIndex + 8);

  return `${beforeUpload}${transformString}/${afterUpload}`;
}

/**
 * Compresse/redimensionne une image côté client avant upload (réseau faible).
 * Retombe silencieusement sur le fichier original si la compression échoue
 * ou n'apporte rien (petit fichier, GIF animé, navigateur sans Canvas API).
 * PRD-robustesse-organisateur-reseau-faible.md (item 3)
 */
async function compressImageForUpload(file: File): Promise<File> {
  if (file.type === 'image/gif' || file.size < COMPRESSION_MIN_SIZE_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const targetWidth = Math.round(bitmap.width * scale);
    const targetHeight = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    if (typeof bitmap.close === 'function') bitmap.close();

    // PNG conservé en PNG (transparence des logos), tout le reste en JPEG.
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outputType, outputType === 'image/jpeg' ? JPEG_QUALITY : undefined)
    );

    if (!blob || blob.size >= file.size) {
      // La compression n'a pas réduit le poids : garder l'original.
      return file;
    }

    const extension = outputType === 'image/png' ? 'png' : 'jpg';
    const compressedName = file.name.replace(/\.\w+$/, '') + `.${extension}`;
    return new File([blob], compressedName, { type: outputType });
  } catch (error) {
    console.warn('⚠️ Compression image échouée, envoi du fichier original:', error);
    return file;
  }
}

/**
 * Un seul essai d'upload, avec AbortController lié à un timeout adaptatif
 * (branche enfin AdaptiveTimeout, jusque-là écrit mais jamais utilisé).
 */
async function uploadImageAttempt(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET!);

  const controller = new AbortController();
  const timeoutMs = AdaptiveTimeout.getApiTimeout() * UPLOAD_TIMEOUT_MULTIPLIER;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(UPLOAD_URL, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    console.error('Réponse Cloudinary non-OK:', response.status, response.statusText);
    throw new Error(`Upload failed (${response.status})`);
  }

  const data = await response.json();
  if (!data.secure_url) {
    console.error('Pas d\'URL sécurisée dans la réponse Cloudinary');
    throw new Error('No secure URL in response');
  }

  return data.secure_url;
}

export const cloudinaryService = {
  async uploadImageFromUrl(imageUrl: string): Promise<string> {
    console.log('Upload Cloudinary depuis URL:', imageUrl.substring(0, 80));

    const formData = new FormData();
    formData.append('file', imageUrl);
    formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET!);

    try {
      const response = await fetch(UPLOAD_URL, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload from URL failed: ${response.status}`);
      }

      const data = await response.json();
      if (!data.secure_url) {
        throw new Error('No secure URL in response');
      }

      console.log('Cloudinary URL depuis remote:', data.secure_url);
      return data.secure_url;
    } catch (error) {
      console.error('Erreur upload Cloudinary depuis URL:', error);
      throw error;
    }
  },

  /**
   * Upload une image vers Cloudinary avec compression client, timeout adaptatif
   * (AdaptiveTimeout) et retry avec backoff exponentiel — pour tenir sur une
   * connexion faible sans pendre indéfiniment ni échouer au premier accroc réseau.
   * PRD-robustesse-organisateur-reseau-faible.md (item 3)
   */
  async uploadImage(file: File): Promise<string> {
    const optimizedFile = await compressImageForUpload(file);
    console.log(
      `Début de l'upload vers Cloudinary: ${file.name} (${file.size}B → ${optimizedFile.size}B)`
    );

    let lastError: unknown;

    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const secureUrl = await uploadImageAttempt(optimizedFile);
        console.log('URL sécurisée Cloudinary:', secureUrl);
        return secureUrl;
      } catch (error) {
        lastError = error;
        const isTimeout = error instanceof DOMException && error.name === 'AbortError';
        console.error(
          `Erreur upload Cloudinary (tentative ${attempt}/${UPLOAD_MAX_ATTEMPTS}):`,
          isTimeout ? 'timeout' : error
        );

        if (attempt < UPLOAD_MAX_ATTEMPTS) {
          // Backoff exponentiel simple, cohérent avec AdaptiveTimeout.createProgressiveTimeout
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Upload failed');
  }
};